import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  FileText, Upload, Trash2, Download, Loader2, Search, Filter,
  FileSpreadsheet, ArrowRight, CheckCircle2, AlertTriangle, Copy, Code,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════
// DOCUMENT VAULT
// ══════════════════════════════════════════════════════════════

const DOC_TYPES = [
  { id: 'contract', label: 'Contract' },
  { id: 'sow', label: 'SOW' },
  { id: 'invoice', label: 'Invoice' },
  { id: 'sla', label: 'SLA' },
  { id: 'amendment', label: 'Amendment' },
  { id: 'other', label: 'Other' },
];

export function DocumentVault({ token, propertyId, platformTypeId, vendorId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (propertyId) params.set('property_id', propertyId);
      if (platformTypeId) params.set('platform_type_id', platformTypeId);
      if (vendorId) params.set('vendor_id', vendorId);
      if (filterType) params.set('doc_type', filterType);
      const res = await axios.get(`${BACKEND}/api/documents?${params}`, { headers });
      setDocs(res.data.documents || []);
    } catch {} finally { setLoading(false); }
  }, [headers, propertyId, platformTypeId, vendorId, filterType]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    if (propertyId) formData.set('property_id', propertyId);
    if (platformTypeId) formData.set('platform_type_id', platformTypeId);
    if (vendorId) formData.set('vendor_id', vendorId);
    setUploading(true);
    try {
      await axios.post(`${BACKEND}/api/documents/upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setUploadOpen(false);
      form.reset();
      fetchDocs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const deletDoc = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await axios.delete(`${BACKEND}/api/documents/${id}`, { headers }); toast.success('Deleted'); fetchDocs(); }
    catch { toast.error('Failed'); }
  };

  const filtered = docs.filter(d => !searchQ || d.title?.toLowerCase().includes(searchQ.toLowerCase()) || d.filename?.toLowerCase().includes(searchQ.toLowerCase()));
  const TYPE_COLORS = { contract: 'bg-blue-100 text-blue-700', sow: 'bg-purple-100 text-purple-700', invoice: 'bg-emerald-100 text-emerald-700', sla: 'bg-amber-100 text-amber-700', amendment: 'bg-orange-100 text-orange-700', other: 'bg-slate-100 text-slate-600' };

  return (
    <div className="space-y-4" data-testid="document-vault">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2"><FileText className="h-4 w-4" /> Documents ({docs.length})</h3>
        <Button size="sm" className="gap-1.5 text-xs h-7" onClick={() => setUploadOpen(true)} data-testid="upload-doc-btn"><Upload className="h-3 w-3" /> Upload</Button>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" /><Input placeholder="Search documents..." value={searchQ} onChange={e => setSearchQ(e.target.value)} className="pl-7 h-8 text-xs" /></div>
        <Select value={filterType} onValueChange={v => setFilterType(v === '__all__' ? '' : v)}><SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue placeholder="All types" /></SelectTrigger><SelectContent><SelectItem value="__all__">All types</SelectItem>{DOC_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent></Select>
      </div>
      {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div> : filtered.length === 0 ? (
        <div className="py-8 text-center text-xs text-muted-foreground">No documents yet. Upload your first contract, SOW, or SLA.</div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map(d => (
            <div key={d.id} className="flex items-center gap-3 p-2.5 rounded-lg border bg-card hover:bg-muted/30 transition-colors" data-testid={`doc-${d.id}`}>
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium truncate block">{d.title || d.filename}</span>
                <span className="text-[10px] text-muted-foreground">{d.property_name}{d.platform_name ? ` / ${d.platform_name}` : ''} — {(d.size / 1024).toFixed(0)}KB</span>
              </div>
              <Badge className={`text-[8px] shrink-0 ${TYPE_COLORS[d.doc_type] || TYPE_COLORS.other}`}>{d.doc_type}</Badge>
              <a href={`${BACKEND}/api/documents/${d.id}/download`} className="shrink-0"><Button variant="ghost" size="icon" className="h-6 w-6"><Download className="h-3 w-3" /></Button></a>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => deletDoc(d.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
            </div>
          ))}
        </div>
      )}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4" /> Upload Document</DialogTitle></DialogHeader>
          <form onSubmit={handleUpload} className="space-y-3 py-2">
            <div><Label className="text-xs">File</Label><Input type="file" name="file" required className="mt-1 text-xs" data-testid="doc-file-input" /></div>
            <div><Label className="text-xs">Title</Label><Input name="title" placeholder="Q4 2025 Contract" className="mt-1" /></div>
            <div><Label className="text-xs">Type</Label>
              <select name="doc_type" className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {DOC_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Notes</Label><Input name="notes" placeholder="Optional notes..." className="mt-1" /></div>
            <DialogFooter><Button variant="ghost" type="button" onClick={() => setUploadOpen(false)}>Cancel</Button><Button type="submit" disabled={uploading} className="gap-1.5" data-testid="doc-upload-submit">{uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// BULK IMPORT WIZARD
// ══════════════════════════════════════════════════════════════

export function BulkImportWizard({ token }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importType, setImportType] = useState('properties');
  const [columnMap, setColumnMap] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const FIELD_OPTIONS = {
    properties: ['name', 'type', 'total_guestrooms', 'number_of_buildings', 'city', 'state', 'brand'],
    assignments: ['property_id', 'platform_type_id', 'product_id', 'vendor_name'],
  };

  const uploadPreview = async () => {
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${BACKEND}/api/admin/import/preview`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      // Auto-map columns by name similarity
      const auto = {};
      for (const col of res.data.columns) {
        const lower = col.toLowerCase().replace(/[^a-z0-9]/g, '');
        const fields = FIELD_OPTIONS[importType] || [];
        const match = fields.find(f => lower.includes(f.replace(/_/g, '')) || f.includes(lower));
        if (match) auto[col] = match;
      }
      setColumnMap(auto);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to parse file'); }
    finally { setLoading(false); }
  };

  const executeImport = async () => {
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_type', importType);
    formData.append('column_map', JSON.stringify(columnMap));
    try {
      const res = await axios.post(`${BACKEND}/api/admin/import/execute`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      setStep(3);
      toast.success(`Imported ${res.data.created} records`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="bulk-import-wizard">
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Bulk Import</h2>
        <p className="text-sm text-muted-foreground mt-1">Import properties or assignments from a CSV file.</p>
      </div>
      <div className="flex gap-2 mb-4">{[1, 2, 3].map(s => <div key={s} className={`flex-1 h-1.5 rounded-full ${s <= step ? 'bg-primary' : 'bg-muted'}`} />)}</div>

      {step === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[['properties', 'Properties', 'Import hotel/property data'], ['assignments', 'Assignments', 'Import platform assignments']].map(([id, label, desc]) => (
              <button key={id} onClick={() => setImportType(id)} className={`p-4 rounded-lg border text-left ${importType === id ? 'border-primary bg-primary/5' : 'hover:bg-muted/60'}`}>
                <span className="text-sm font-semibold block">{label}</span>
                <span className="text-[10px] text-muted-foreground">{desc}</span>
              </button>
            ))}
          </div>
          <div className="border-2 border-dashed rounded-xl p-8 text-center">
            <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <Input type="file" accept=".csv,.txt" onChange={e => setFile(e.target.files?.[0])} className="max-w-xs mx-auto" data-testid="import-file-input" />
            <p className="text-xs text-muted-foreground mt-2">CSV files with headers</p>
          </div>
          <Button onClick={uploadPreview} disabled={!file || loading} className="gap-2" data-testid="import-preview-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Preview & Map Columns
          </Button>
        </div>
      )}

      {step === 2 && preview && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{preview.total_rows} rows found in {preview.filename}</span>
            <Button variant="outline" size="sm" onClick={() => setStep(1)}>Back</Button>
          </div>
          <div className="border rounded-lg p-4 space-y-3">
            <h4 className="text-xs font-semibold uppercase text-muted-foreground">Column Mapping</h4>
            {preview.columns.map(col => (
              <div key={col} className="flex items-center gap-3">
                <span className="text-xs font-mono w-40 truncate bg-muted/40 px-2 py-1 rounded">{col}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                <Select value={columnMap[col] || '__skip__'} onValueChange={v => setColumnMap(p => ({ ...p, [col]: v === '__skip__' ? '' : v }))}>
                  <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__skip__">Skip column</SelectItem>
                    {(FIELD_OPTIONS[importType] || []).map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-[10px]">
              <thead className="bg-muted/40"><tr>{preview.columns.map(c => <th key={c} className="p-2 text-left font-semibold">{c}</th>)}</tr></thead>
              <tbody>{preview.preview_rows.slice(0, 5).map((row, i) => <tr key={i} className="border-t">{preview.columns.map(c => <td key={c} className="p-2 truncate max-w-[120px]">{row[c]}</td>)}</tr>)}</tbody>
            </table>
          </div>
          <Button onClick={executeImport} disabled={importing} className="gap-2" data-testid="import-execute-btn">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Import {preview.total_rows} {importType}
          </Button>
        </div>
      )}

      {step === 3 && result && (
        <div className="space-y-4 text-center py-6">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
          <h3 className="text-lg font-bold">Import Complete</h3>
          <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
            <div className="p-3 rounded-lg border bg-card"><div className="text-xl font-bold text-emerald-600">{result.created}</div><div className="text-[10px] text-muted-foreground">Created</div></div>
            <div className="p-3 rounded-lg border bg-card"><div className="text-xl font-bold text-amber-600">{result.skipped}</div><div className="text-[10px] text-muted-foreground">Skipped</div></div>
            <div className="p-3 rounded-lg border bg-card"><div className="text-xl font-bold text-red-600">{result.errors?.length || 0}</div><div className="text-[10px] text-muted-foreground">Errors</div></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="text-left max-w-sm mx-auto border rounded-lg p-3 bg-red-50">
              {result.errors.slice(0, 5).map((e, i) => <p key={i} className="text-[10px] text-red-600">Row {e.row}: {e.error}</p>)}
            </div>
          )}
          <Button onClick={() => { setStep(1); setFile(null); setPreview(null); setResult(null); }} className="gap-2">Import More</Button>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// EMBED CODE GENERATOR
// ══════════════════════════════════════════════════════════════

export function EmbedCodeGenerator({ token, tenantId }) {
  const [embedCode, setEmbedCode] = useState(null);
  const [theme, setTheme] = useState('light');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const baseUrl = BACKEND;

  useEffect(() => {
    if (!tenantId) return;
    axios.get(`${BACKEND}/api/embed/code/${tenantId}`, { headers })
      .then(r => setEmbedCode(r.data))
      .catch(() => {});
  }, [tenantId, headers]);

  const iframeSrc = `${baseUrl}/api/embed/widget/${tenantId}?theme=${theme}`;
  const iframeCode = `<iframe src="${iframeSrc}" width="400" height="180" frameborder="0" style="border-radius:12px;border:1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};"></iframe>`;

  const copyCode = () => {
    navigator.clipboard.writeText(iframeCode);
    toast.success('Embed code copied to clipboard');
  };

  if (!tenantId) return <p className="text-sm text-muted-foreground text-center py-8">Select a tenant to generate embed code.</p>;

  return (
    <div className="space-y-4" data-testid="embed-generator">
      <h3 className="text-sm font-semibold flex items-center gap-2"><Code className="h-4 w-4" /> Embeddable Widget</h3>
      <p className="text-xs text-muted-foreground">Embed a read-only KPI widget on your intranet, SharePoint, or any webpage.</p>
      <div className="flex gap-2">
        {['light', 'dark'].map(t => (
          <button key={t} onClick={() => setTheme(t)} className={`px-3 py-1.5 rounded-md text-xs ${theme === t ? 'bg-primary text-primary-foreground' : 'bg-muted/60 text-muted-foreground'}`}>{t === 'light' ? 'Light' : 'Dark'}</button>
        ))}
      </div>
      <div className="border rounded-xl overflow-hidden bg-muted/20 p-4">
        <iframe src={iframeSrc} width="400" height="180" frameBorder="0" style={{ borderRadius: '12px', border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E2E8F0' }} title="Embed preview" />
      </div>
      <div className="relative">
        <pre className="p-3 rounded-lg bg-muted/40 border text-[10px] font-mono overflow-x-auto whitespace-pre-wrap break-all">{iframeCode}</pre>
        <Button variant="outline" size="sm" className="absolute top-2 right-2 h-6 text-[9px] gap-1" onClick={copyCode} data-testid="copy-embed-code"><Copy className="h-2.5 w-2.5" /> Copy</Button>
      </div>
    </div>
  );
}
